"use client";

import { useParams } from "next/navigation";
import Link from "next/link";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { useState } from "react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { usePageTitle } from "@/lib/usePageTitle";
import { useAuth, SignInButton } from "@clerk/clerk-react";
import {
  ServerIcon,
  GlobeIcon,
  CopyIcon,
  CheckIcon,
  ArrowRightIcon,
} from "@/components/icons";

export default function ClaimServerPage() {
  const params = useParams();
  const slug = params.slug as string;

  const { isSignedIn } = useAuth();
  const listing = useQuery(api.serverListings.getBySlug, { slug });
  const requestClaim = useMutation(api.serverListings.requestClaim);

  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{
    verificationToken: string;
    verificationCommand: string;
    expiresAt: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copiedCommand, setCopiedCommand] = useState(false);

  usePageTitle(listing ? `Claim ${listing.name}` : "Claim Server");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!listing) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await requestClaim({
        listingId: listing._id,
        notes: notes.trim() || undefined,
      });
      setResult({
        verificationToken: res.verificationToken,
        verificationCommand: res.verificationCommand,
        expiresAt: res.expiresAt,
      });
    } catch (err) {
      setError((err as Error)?.message ?? "Failed to submit claim. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function copyCommand() {
    if (!result) return;
    await navigator.clipboard.writeText(result.verificationCommand);
    setCopiedCommand(true);
    setTimeout(() => setCopiedCommand(false), 2000);
  }

  // Loading
  if (listing === undefined) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Navbar />
        <main className="flex-1 py-12">
          <div className="mx-auto max-w-xl px-4 sm:px-6 space-y-4">
            <Skeleton className="h-10 w-64" />
            <Skeleton className="h-64" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Not found
  if (listing === null) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Navbar />
        <main className="flex-1 py-20 text-center">
          <GlobeIcon className="mx-auto h-12 w-12 text-muted" />
          <h1 className="mt-4 text-2xl font-bold text-foreground">Server not found</h1>
          <p className="mt-2 text-muted">This listing may not exist or has been removed.</p>
          <Link href="/servers" className="mt-6 inline-block">
            <Button variant="outline">← Back to Servers</Button>
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  // Already claimed
  if (listing.status === "claimed") {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Navbar />
        <main className="flex-1 py-20 text-center">
          <CheckIcon className="mx-auto h-12 w-12 text-primary" />
          <h1 className="mt-4 text-2xl font-bold text-foreground">Already Claimed</h1>
          <p className="mt-2 text-muted">This server has already been claimed by its owner.</p>
          <Link href={`/servers/${slug}`} className="mt-6 inline-block">
            <Button variant="outline">← Back to Server</Button>
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const serverAddress = listing.serverIp
    ? listing.serverPort ? `${listing.serverIp}:${listing.serverPort}` : listing.serverIp
    : null;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />
      <main className="flex-1 py-10">
        <div className="mx-auto max-w-xl px-4 sm:px-6 space-y-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-muted">
            <Link href="/servers" className="hover:text-foreground transition-colors">Servers</Link>
            <span>/</span>
            <Link href={`/servers/${slug}`} className="hover:text-foreground transition-colors">{listing.name}</Link>
            <span>/</span>
            <span className="text-foreground">Claim</span>
          </div>

          {/* Header */}
          <div>
            <h1 className="text-2xl font-bold text-foreground">Claim Server Listing</h1>
            <p className="mt-1 text-muted">
              Verify ownership of <span className="font-medium text-foreground">{listing.name}</span> and take control of this listing.
            </p>
          </div>

          {/* Server summary */}
          <Card>
            <CardContent className="py-4 flex items-center gap-3">
              <ServerIcon className="h-8 w-8 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground truncate">{listing.name}</p>
                {serverAddress && (
                  <p className="text-sm text-muted font-mono">{serverAddress}</p>
                )}
              </div>
              <Badge variant={listing.status === "pending_claim" ? "secondary" : "outline"}>
                {listing.status === "pending_claim" ? "Pending" : "Unclaimed"}
              </Badge>
            </CardContent>
          </Card>

          {/* Auth gate */}
          {!isSignedIn ? (
            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="py-6 text-center space-y-3">
                <GlobeIcon className="mx-auto h-10 w-10 text-primary" />
                <h2 className="font-semibold text-foreground">Sign in to claim this server</h2>
                <p className="text-sm text-muted">You need an account to verify ownership and claim this listing.</p>
                <div className="flex justify-center gap-3 flex-wrap">
                  <SignInButton mode="modal">
                    <Button>Sign In</Button>
                  </SignInButton>
                  <Link href="/signup">
                    <Button variant="outline">Create Account</Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ) : result ? (
            /* Verification instructions */
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base text-primary">
                  <CheckIcon className="h-5 w-5" />
                  Claim Submitted — Verify Now
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted">
                  To verify ownership, run the following command in-game on <strong>{listing.name}</strong> as an OP or server admin:
                </p>

                <div className="relative rounded-lg border border-border bg-foreground/5 p-4">
                  <code className="text-sm font-mono text-foreground break-all">
                    {result.verificationCommand}
                  </code>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-2 h-8 w-8"
                    onClick={copyCommand}
                    aria-label="Copy verification command"
                  >
                    {copiedCommand ? (
                      <CheckIcon className="h-4 w-4 text-primary" />
                    ) : (
                      <CopyIcon className="h-4 w-4" />
                    )}
                  </Button>
                </div>

                <div className="rounded-lg border border-border p-3 space-y-1 text-sm">
                  <p className="text-muted">
                    <strong className="text-foreground">Token:</strong>{" "}
                    <code className="font-mono">{result.verificationToken}</code>
                  </p>
                  <p className="text-muted">
                    <strong className="text-foreground">Expires:</strong>{" "}
                    {new Date(result.expiresAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>

                <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3 text-sm text-amber-800 dark:text-amber-200">
                  <strong>Manual verification:</strong> If you prefer, email{" "}
                  <a href="mailto:support@blockynetworks.com" className="underline">
                    support@blockynetworks.com
                  </a>{" "}
                  from your account email with proof of server ownership.
                </div>

                <p className="text-xs text-muted">
                  Once verified, your claim will be reviewed and approved. You&apos;ll receive a notification when it&apos;s live.
                </p>

                <Link href={`/servers/${slug}`}>
                  <Button variant="outline" className="w-full">
                    Back to Server Listing
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            /* Claim form */
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Claim Verification</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="rounded-lg bg-foreground/5 border border-border p-4 text-sm space-y-2">
                    <p className="font-medium text-foreground">How it works:</p>
                    <ol className="list-decimal list-inside space-y-1 text-muted">
                      <li>Submit this form to generate a verification command</li>
                      <li>Run the command in-game on your server as OP</li>
                      <li>Your claim will be reviewed and approved</li>
                    </ol>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-foreground" htmlFor="notes">
                      Additional notes <span className="text-muted font-normal">(optional)</span>
                    </label>
                    <textarea
                      id="notes"
                      placeholder="Any extra context to help us verify your ownership..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                    />
                  </div>

                  {error && (
                    <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                      {error}
                    </div>
                  )}

                  <Button type="submit" className="w-full" disabled={submitting}>
                    {submitting ? "Submitting…" : "Generate Verification Command"}
                    {!submitting && <ArrowRightIcon className="ml-2 h-4 w-4" />}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
